import { Link } from 'react-router-dom';
import { Mail, MessageCircle, ArrowUpRight } from 'lucide-react';
import { site } from '../data/site';
import { services } from '../data/services';
import { Logo } from './Navbar';
import { Reveal } from './Reveal';

export const Footer = () => (
  <footer data-testid="site-footer" className="grain relative overflow-hidden bg-pine text-white">
    <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-emerald-600/30 blur-[120px]" aria-hidden="true" />
    <div className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle_at_center,rgba(217,70,239,0.16),transparent_65%)] blur-3xl" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
      <Reveal>
        <div className="mb-16 flex flex-col items-start justify-between gap-8 border-b border-white/10 pb-14 lg:flex-row lg:items-end">
          <div className="max-w-xl">
            <p className="eyebrow mb-4 text-goldlight">Ready to Grow?</p>
            <h2 className="font-display text-4xl font-bold leading-[1.1] sm:text-5xl">
              Let's Talk.
            </h2>
            <p className="mt-4 text-white">
              One Hub. Many Solutions. Maximum Impact.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" data-testid="footer-cta-contact" className="btn-mint">
              Let's Grow Your Business <ArrowUpRight size={17} />
            </Link>
            <a href={site.whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="footer-cta-whatsapp" className="btn-outline-light">
              <MessageCircle size={17} /> WhatsApp
            </a>
          </div>
        </div>
      </Reveal>

      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo dark />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-white">
            {site.message} Helping brands build visibility, attract opportunities, and achieve
            sustainable digital growth.
          </p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="footer-socials">
            {site.socials.map((s) =>
              s.url ? (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-link-${s.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className="rounded-full border border-white/20 px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:border-gold hover:text-goldlight"
                >
                  {s.label}
                </a>
              ) : (
                <span
                  key={s.label}
                  data-testid={`social-placeholder-${s.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className="rounded-full border border-white/10 px-3.5 py-1.5 text-xs font-semibold text-white"
                  title="Official link coming soon"
                >
                  {s.label}
                </span>
              )
            )}
          </div>
        </div>

        <nav aria-label="Footer quick links">
          <h3 className="mb-5 font-mono text-xs uppercase tracking-[0.24em] text-goldlight">Quick Links</h3>
          <ul className="space-y-3">
            {site.nav.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase()}`}
                  className="text-sm text-white transition-colors hover:text-goldlight"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Footer services">
          <h3 className="mb-5 font-mono text-xs uppercase tracking-[0.24em] text-goldlight">Our Services</h3>
          <ul className="space-y-3">
            {services.map((s) => (
              <li key={s.slug}>
                <Link
                  to={s.linkOverride || `/services/${s.slug}`}
                  data-testid={`footer-service-${s.slug}`}
                  className="text-sm text-white transition-colors hover:text-goldlight"
                >
                  {s.shortTitle}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h3 className="mb-5 font-mono text-xs uppercase tracking-[0.24em] text-goldlight">Contact</h3>
          <a
            href={`mailto:${site.email}`}
            data-testid="footer-email"
            className="mb-4 flex items-center gap-3 text-sm text-white transition-colors hover:text-goldlight"
          >
            <Mail size={16} className="shrink-0 text-goldlight" /> {site.email}
          </a>
          <a
            href={site.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-whatsapp"
            className="flex items-center gap-3 text-sm text-white transition-colors hover:text-goldlight"
          >
            <MessageCircle size={16} className="shrink-0 text-goldlight" /> WhatsApp: {site.whatsappDisplay}
          </a>
        </div>
      </div>

      <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white sm:flex-row sm:items-center">
        <p data-testid="footer-copyright">© {new Date().getFullYear()} TheBigHub. All rights reserved.</p>
        <div className="flex gap-6">
          <Link to="/privacy" data-testid="footer-link-privacy" className="transition-colors hover:text-goldlight">
            Privacy Policy
          </Link>
          <Link to="/terms" data-testid="footer-link-terms" className="transition-colors hover:text-goldlight">
            Terms & Conditions
          </Link>
        </div>
      </div>
    </div>
  </footer>
);
